import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { GrantDetailsModal } from "@/components/GrantDetailsModal";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Bookmark, Trash2, Loader2, Building2, Euro, CalendarClock, ExternalLink, Plus, StickyNote, Check, ListChecks, ChevronDown,
} from "lucide-react";
import { scoreTone } from "@/lib/constants";

const STATI = ["Da Valutare", "In Preparazione", "Inviato"];

function ChecklistEditor({ fav, onSave }) {
  const docs = fav.grant?.documentazione || [];
  const [open, setOpen] = useState(false);
  if (!Array.isArray(docs) || docs.length === 0) return null;

  const checklist = fav.checklist || {};
  const done = docs.filter((d) => checklist[d]).length;
  const total = docs.length;
  const pct = Math.round((done / total) * 100);

  const toggle = (doc) => {
    onSave(fav, { ...checklist, [doc]: !checklist[doc] });
  };

  return (
    <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="favorite-checklist-toggle"
        className="flex w-full items-center justify-between gap-2"
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
          <ListChecks className="h-4 w-4 text-indigo-500" />
          Checklist documenti
          <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
            done === total ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
          }`}>
            {done}/{total}
          </span>
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div className={`h-full rounded-full transition-all ${done === total ? "bg-emerald-500" : "bg-indigo-500"}`}
          style={{ width: `${pct}%` }} />
      </div>

      {open && (
        <ul className="mt-3 space-y-2" data-testid="favorite-checklist-items">
          {docs.map((doc, i) => {
            const checked = Boolean(checklist[doc]);
            return (
              <li key={i} className="flex items-start gap-2.5">
                <Checkbox
                  id={`${fav.id}-doc-${i}`}
                  checked={checked}
                  onCheckedChange={() => toggle(doc)}
                  data-testid="checklist-item-checkbox"
                  className="mt-0.5"
                />
                <label
                  htmlFor={`${fav.id}-doc-${i}`}
                  className={`cursor-pointer text-sm ${checked ? "text-slate-400 line-through" : "text-slate-700"}`}
                >
                  {doc}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NoteEditor({ fav, onSave }) {
  const [draft, setDraft] = useState(fav.nota || "");
  const [open, setOpen] = useState(Boolean(fav.nota));
  const dirty = draft !== (fav.nota || "");

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="favorite-add-note-button"
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:underline"
      >
        <StickyNote className="h-3.5 w-3.5" /> Aggiungi una nota
      </button>
    );
  }

  return (
    <div className="mt-3">
      <span className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <StickyNote className="h-3.5 w-3.5" /> Nota personale
      </span>
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        placeholder="Es. Preparare visura, business plan, preventivi..."
        data-testid="favorite-note-input"
        className="text-sm"
      />
      {dirty && (
        <Button
          size="sm"
          className="mt-2 h-8 gap-1.5"
          onClick={() => onSave(fav, draft)}
          data-testid="favorite-note-save-button"
        >
          <Check className="h-3.5 w-3.5" /> Salva nota
        </Button>
      )}
    </div>
  );
}

const statoBadge = {
  "Da Valutare": "bg-slate-100 text-slate-600",
  "In Preparazione": "bg-amber-50 text-amber-700",
  "Inviato": "bg-emerald-50 text-emerald-700",
};

export default function Favorites() {
  const [favorites, setFavorites] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filterCompany, setFilterCompany] = useState("all");

  const load = async () => {
    const [f, c] = await Promise.all([api.get("/favorites"), api.get("/companies")]);
    setFavorites(f.data);
    setCompanies(c.data);
  };

  useEffect(() => { load(); }, []);

  const companyName = (cid) => companies.find((c) => c.id === cid)?.ragione_sociale || "Azienda";

  const changeStatus = async (fav, stato) => {
    setFavorites((prev) => prev.map((f) => (f.id === fav.id ? { ...f, stato } : f)));
    try {
      await api.put(`/favorites/${fav.id}`, { stato });
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Errore aggiornamento stato");
      load();
    }
  };

  const remove = async (fav) => {
    try {
      await api.delete(`/favorites/${fav.id}`);
      toast.success("Rimosso dai preferiti");
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const saveNote = async (fav, nota) => {
    setFavorites((prev) => prev.map((f) => (f.id === fav.id ? { ...f, nota } : f)));
    try {
      await api.put(`/favorites/${fav.id}`, { nota });
      toast.success("Nota salvata");
    } catch {
      toast.error("Errore salvataggio nota");
      load();
    }
  };

  const saveChecklist = async (fav, checklist) => {
    setFavorites((prev) => prev.map((f) => (f.id === fav.id ? { ...f, checklist } : f)));
    try {
      await api.put(`/favorites/${fav.id}`, { checklist });
    } catch {
      toast.error("Errore aggiornamento checklist");
      load();
    }
  };

  const filtered = useMemo(() => {
    if (!favorites) return [];
    return filterCompany === "all" ? favorites : favorites.filter((f) => f.company_id === filterCompany);
  }, [favorites, filterCompany]);

  if (favorites === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="eyebrow text-indigo-600">Bandi salvati</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Preferiti</h1>
          <p className="mt-1 text-slate-500">Monitora lo stato di preparazione delle tue domande.</p>
        </div>
        {favorites.length > 0 && (
          <Select value={filterCompany} onValueChange={setFilterCompany}>
            <SelectTrigger className="sm:w-64" data-testid="favorites-company-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le aziende</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {favorites.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="empty-favorites">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100">
            <Bookmark className="h-7 w-7 text-slate-400" />
          </span>
          <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Nessun bando salvato</h3>
          <p className="mt-1 max-w-sm text-sm text-slate-500">Avvia una ricerca AI e salva i bandi che ti interessano.</p>
          <Button asChild className="mt-6 gap-2"><Link to="/app"><Plus className="h-4 w-4" /> Vai alle aziende</Link></Button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {filtered.map((f) => {
            const g = f.grant || {};
            const tone = scoreTone(g.compatibilita || 0);
            return (
              <div key={f.id} data-testid="favorite-card" className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">{g.ente}</span>
                    <span className={`rounded-md border px-2 py-1 text-[11px] font-bold ${tone.cls}`}>{g.compatibilita}%</span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                      <Building2 className="h-3 w-3" />{companyName(f.company_id)}
                    </span>
                  </div>
                  <button onClick={() => remove(f)} data-testid="favorite-remove-button" className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <h3 className="mt-3 font-display text-lg font-bold text-slate-900">{g.titolo}</h3>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                  <span className="flex items-center gap-1.5"><Euro className="h-4 w-4 text-emerald-600" />{g.importo_max}</span>
                  <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-amber-500" />{g.scadenza}</span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-400">Stato:</span>
                    <Select value={f.stato} onValueChange={(v) => changeStatus(f, v)}>
                      <SelectTrigger className="h-8 w-44" data-testid="favorite-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${statoBadge[f.stato] || "bg-slate-100 text-slate-600"}`}>
                    {f.stato}
                  </span>
                </div>

                <NoteEditor fav={f} onSave={saveNote} />
                <ChecklistEditor fav={f} onSave={saveChecklist} />

                <div className="mt-auto flex gap-2 pt-5">
                  <Button variant="secondary" className="flex-1" onClick={() => setSelected(g)} data-testid="favorite-details-button">
                    Dettagli & Analisi
                  </Button>
                  {g.link_ufficiale && (
                    <Button asChild variant="outline" className="gap-1.5">
                      <a href={g.link_ufficiale} target="_blank" rel="noopener noreferrer">
                        Portale <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <GrantDetailsModal grant={selected} open={Boolean(selected)} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
